/** Parse RAG generate/get-solution payloads (shape aligned with mobile ChatModule). */

function hasSolutionKeys(o: Record<string, unknown>): boolean {
  return (
    typeof o.answer === "string" ||
    Array.isArray(o.hints) ||
    Array.isArray(o.steps) ||
    Array.isArray((o as { solution_steps?: unknown }).solution_steps)
  );
}

/** Walk `data` wrappers until we find answer / hints / steps (or give up). */
function peelToSolutionShape(
  root: Record<string, unknown>,
): Record<string, unknown> {
  let cur: Record<string, unknown> = root;
  for (let i = 0; i < 5; i++) {
    if (hasSolutionKeys(cur)) return cur;
    const inner = cur.data;
    if (inner != null && typeof inner === "object") {
      cur = inner as Record<string, unknown>;
    } else {
      break;
    }
  }
  return cur;
}

function coerceStringList(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return [];
    if (t.startsWith("[")) {
      try {
        const parsed = JSON.parse(t) as unknown;
        return coerceStringList(parsed);
      } catch {
        /* use as single line */
      }
    }
    return t
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === "string") {
      const s = item.trim();
      if (s) out.push(s);
    } else if (item != null && typeof item === "object") {
      const o = item as Record<string, unknown>;
      const cand = [o.text, o.hint, o.content, o.description, o.message].find(
        (x) => typeof x === "string" && x.trim().length > 0,
      );
      if (typeof cand === "string") out.push(cand.trim());
    }
  }
  return out;
}

export function extractRagSolution(raw: unknown): {
  answer: string;
  hints: string[];
  steps: string[];
} {
  if (raw == null || typeof raw !== "object") {
    return { answer: "", hints: [], steps: [] };
  }
  const data = peelToSolutionShape(raw as Record<string, unknown>);

  const answer = typeof data.answer === "string" ? data.answer : "";
  const hints = coerceStringList(data.hints);
  const stepsRaw =
    data.steps ?? (data as { solution_steps?: unknown }).solution_steps;
  const steps = coerceStringList(stepsRaw);

  return { answer, hints, steps };
}
